import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'

import { DisputeReason } from '../core/response.types'
import { createReason, patchReason } from '../core/api'

interface Props {
  show: boolean
  onClose: () => void
  editing?: DisputeReason | null
  /** Position for a new reason — it lands at the end of the dictionary. */
  nextSortOrder: number
  onSaved: () => void
}

interface FormState {
  code: string
  title: string
  enabled: boolean
  sortOrder: number
}

const empty: FormState = { code: '', title: '', enabled: true, sortOrder: 0 }

export const ReasonModal: React.FC<Props> = ({
  show,
  onClose,
  editing,
  nextSortOrder,
  onSaved,
}) => {
  const isEdit = !!editing
  const [form, setForm] = useState<FormState>(empty)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!show) return
    setError(null)
    if (editing) {
      setForm({
        code: editing.code,
        title: editing.title,
        enabled: editing.enabled,
        sortOrder: editing.sortOrder,
      })
    } else {
      // Порядок больше не вводится руками — новая причина встаёт в конец справочника.
      setForm({ ...empty, sortOrder: nextSortOrder })
    }
  }, [show, editing, nextSortOrder])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      if (isEdit && editing) {
        const r = await patchReason(editing.id, {
          title: form.title,
          enabled: form.enabled,
          sortOrder: form.sortOrder,
        })
        if (!r.data.success) {
          setError(buildErrorText(r.data))
          return
        }
      } else {
        const r = await createReason({
          code: form.code,
          title: form.title,
          sortOrder: form.sortOrder,
        })
        if (!r.data.success) {
          setError(buildErrorText(r.data))
          return
        }
      }
      onSaved()
      onClose()
    } catch (e: unknown) {
      // Доменные ошибки приходят 4xx-ответом, axios его реджектит: тело — в response.data.
      const data = (e as { response?: { data?: ApiErrorBody } })?.response?.data
      setError(buildErrorText(data))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Редактирование причины' : 'Новая причина диспута'}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className='mb-3'>
            <label className='form-label required'>Код</label>
            <input
              type='text'
              className='form-control'
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              disabled={isEdit}
              required={!isEdit}
              maxLength={64}
              placeholder='FAKE_RECEIPT'
            />
            {isEdit && <div className='text-muted small'>Код нельзя изменить после создания</div>}
          </div>
          <div className='mb-3'>
            <label className='form-label required'>Название</label>
            <input
              type='text'
              className='form-control'
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              maxLength={255}
            />
          </div>
          {isEdit && (
            <div className='form-check mb-3'>
              <input
                id='enabledToggle'
                type='checkbox'
                className='form-check-input'
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              />
              <label className='form-check-label' htmlFor='enabledToggle'>
                Активна (показывается в lookup)
              </label>
            </div>
          )}
          {error && <div className='alert alert-danger py-2'>{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={saving}>
            Отмена
          </button>
          <button type='submit' className='btn btn-primary' disabled={saving}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

interface ApiErrorBody {
  errorCode?: string
  errors?: Record<string, string>
}

// Бэкенд отдаёт машиночитаемый errorCode (CPAYMENT-1591) и general_error английским текстом —
// показываем оператору человекочитаемое сообщение по коду, а errors оставляем фолбэком
// для валидации полей.
const ERROR_MESSAGE: Record<string, string> = {
  DISPUTE_REASON_EXISTS: 'Причина с таким кодом уже существует',
  DISPUTE_REASON_NOT_FOUND: 'Причина не найдена — обновите страницу',
}

function buildErrorText(data?: ApiErrorBody): string {
  const byCode = data?.errorCode ? ERROR_MESSAGE[data.errorCode] : undefined
  if (byCode) return byCode
  if (data?.errors?.general_error) return data.errors.general_error
  const first = data?.errors ? Object.entries(data.errors)[0] : undefined
  return first ? `${first[0]}: ${first[1]}` : 'Не удалось сохранить причину'
}

export default ReasonModal
